import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlertTriangle, FlaskConical, Pencil, Search, Sparkles, Wrench } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import ToolEditDrawer from './ToolEditDrawer';
import EnrichDiffDialog from './EnrichDiffDialog';
import {
  JOB_STATUS_TOOL,
  TOOL_FILTERS,
  TOOL_SOFT_LIMIT,
  countEnabled,
  describeError,
  filterTools,
  findDuplicateToolNames,
  INVALID_SCHEMA_COPY,
  getToolBadges,
  invalidToolsById,
  isToolReadOnly,
  supportsLongRunning,
} from '../mcpUtils';

const BADGE_CLASS = {
  danger: styles.badgeDanger,
  warning: styles.badgeWarning,
  neutral: styles.badgeNeutral,
};

/**
 * Curate tools (§4) — a SELECTION screen, not a confirmation screen.
 *
 * When an import returns more than ~40 candidates the API enables none of them,
 * because past that point model tool-selection degrades in both Claude and GPT
 * and the client's context fills before the user types anything. If this screen
 * read as "42 tools imported ✓" the customer would publish an empty server, so
 * the framing throughout is "found — now choose".
 */
const FOCUS_HIGHLIGHT_MS = 2500;

const CurateToolsTab = ({
  server,
  tools: allTools,
  onMutated,
  onTestTool,
  notify,
  invalidTools = [],
  focusToolId = null,
  onFocusHandled,
}) => {
  // `get_job_status` is added by the platform while deferral is on — never a
  // stored tool, so there is nothing to enable, disable or edit here.
  const tools = useMemo(
    () =>
      supportsLongRunning(server)
        ? allTools.filter((t) => t?.tool_name !== JOB_STATUS_TOOL)
        : allTools,
    [allTools, server]
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [pending, setPending] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  // Not an effect cleanup: handing the focus back clears `focusToolId`, which
  // would re-run the effect and cancel these before they fire.
  const focusTimersRef = useRef([]);
  useEffect(() => () => focusTimersRef.current.forEach(clearTimeout), []);

  // Lint only checks enabled tools, so a reason on a disabled one is stale.
  const invalidById = useMemo(() => invalidToolsById(invalidTools), [invalidTools]);
  const invalidReason = (tool) =>
    tool?.enabled ? invalidById.get(String(tool.id)) || null : null;

  const enabledCount = countEnabled(tools);
  const duplicates = useMemo(() => findDuplicateToolNames(tools), [tools]);
  const visible = useMemo(() => filterTools(tools, { search, filter }), [tools, search, filter]);

  // Arriving from a lint link: make sure the tool is visible, scroll to it and
  // open its editor — the schema is what needs fixing.
  useEffect(() => {
    if (focusToolId == null) return;
    const target = tools.find((t) => String(t.id) === String(focusToolId));
    onFocusHandled?.();
    if (!target) {
      notify('That tool is no longer on this server.', 'info');
      return;
    }
    setSearch('');
    setFilter('all');
    setEditingTool(target);
    setHighlightId(String(target.id));
    focusTimersRef.current.forEach(clearTimeout);
    focusTimersRef.current = [
      setTimeout(() => {
        document
          .getElementById(`mcp-tool-${target.id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50),
      setTimeout(() => setHighlightId(null), FOCUS_HIGHLIGHT_MS),
    ];
  }, [focusToolId, tools, onFocusHandled, notify]);

  const nothingEnabled = tools.length > 0 && enabledCount === 0;
  const overLimit = enabledCount > TOOL_SOFT_LIMIT;

  const setEnabled = async (toolNames, enabled) => {
    if (!toolNames.length) return;
    const names = new Set(toolNames);
    setPending((p) => new Set([...p, ...names]));
    try {
      await mcpServerService.setToolsEnabled(server.id, toolNames, enabled);
      await onMutated();
    } catch (e) {
      notify(describeError(e, "Couldn't update the tool selection."), 'error');
    } finally {
      setPending((p) => {
        const next = new Set(p);
        names.forEach((n) => next.delete(n));
        return next;
      });
    }
  };

  const runBulk = async (toolNames, enabled) => {
    setBulkBusy(true);
    await setEnabled(toolNames, enabled);
    setBulkBusy(false);
  };

  const handleEnableAll = () => runBulk(tools.map((t) => t.tool_name), true);
  const handleEnableReadOnly = () =>
    runBulk(tools.filter(isToolReadOnly).map((t) => t.tool_name), true);
  const handleEnableVisible = () => runBulk(visible.map((t) => t.tool_name), true);

  const meterClass = nothingEnabled
    ? styles.countMeterEmpty
    : overLimit
    ? styles.countMeterOver
    : '';

  const readOnlyCount = tools.filter(isToolReadOnly).length;

  if (tools.length === 0) {
    return (
      <div className={sharedStyles.emptyState}>
        <Wrench size={44} className={sharedStyles.emptyIcon} />
        <Typography variant="h6">No tools yet</Typography>
        <Typography variant="body2" className={sharedStyles.muted}>
          {server.server_type === 'api'
            ? 'Import a spec on the Import tab to generate candidate tools.'
            : 'Tools are created for this server type — try refreshing in a moment.'}
        </Typography>
      </div>
    );
  }

  return (
    <>
      {/* The framing that decides whether this feature works: found, not imported. */}
      {nothingEnabled && (
        <div className={styles.curateBanner}>
          <div className={styles.curateBannerText}>
            <Typography className={styles.curateBannerTitle}>
              {tools.length} tools found — choose which to expose
            </Typography>
            <span className={styles.curateBannerHint}>
              None are exposed yet. Past about {TOOL_SOFT_LIMIT} enabled tools, models start picking
              the wrong one and the client’s context fills before the user types anything — so pick
              the ones this connector is actually for.
            </span>
          </div>
          <div className={styles.curateBannerActions}>
            {readOnlyCount > 0 && (
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                size="small"
                onClick={handleEnableReadOnly}
                disabled={bulkBusy}
              >
                Enable {readOnlyCount} read-only
              </Button>
            )}
            <Button
              className={sharedStyles.outlineBtn}
              variant="outlined"
              size="small"
              onClick={handleEnableAll}
              disabled={bulkBusy}
            >
              Enable all {tools.length}
            </Button>
          </div>
        </div>
      )}

      <div className={sharedStyles.toolbar}>
        <div className={sharedStyles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search tools"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 8 }} /> }}
            sx={{ minWidth: 240 }}
          />
          <div className={styles.filterChips}>
            {TOOL_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`${styles.filterChip} ${filter === f.value ? styles.filterChipActive : ''}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            className={sharedStyles.outlineBtn}
            variant="outlined"
            size="small"
            startIcon={<Sparkles size={15} />}
            onClick={() => setEnrichOpen(true)}
          >
            Improve descriptions
          </Button>
          <Tooltip
            title={
              overLimit
                ? `Past ${TOOL_SOFT_LIMIT} tools, models start picking the wrong one.`
                : 'Only enabled tools are exposed to models.'
            }
          >
            <span className={`${styles.countMeter} ${meterClass}`}>
              {bulkBusy && <CircularProgress size={12} color="inherit" />}
              {enabledCount} of {tools.length} enabled
            </span>
          </Tooltip>
        </Box>
      </div>

      {overLimit && (
        <div className={styles.curateBanner}>
          <div className={styles.curateBannerText}>
            <Typography className={styles.curateBannerTitle}>
              {enabledCount} tools enabled — more than models handle well
            </Typography>
            <span className={styles.curateBannerHint}>
              Above roughly {TOOL_SOFT_LIMIT}, tool selection degrades in both Claude and GPT.
              Consider turning off the ones this connector isn’t for.
            </span>
          </div>
        </div>
      )}

      <div className={sharedStyles.tableCard}>
        <div className={styles.toolRow} style={{ background: 'var(--bg-hover)' }}>
          <Checkbox
            size="small"
            checked={visible.length > 0 && visible.every((t) => t.enabled)}
            indeterminate={visible.some((t) => t.enabled) && !visible.every((t) => t.enabled)}
            onChange={(e) =>
              e.target.checked
                ? handleEnableVisible()
                : runBulk(visible.filter((t) => t.enabled).map((t) => t.tool_name), false)
            }
            disabled={bulkBusy || visible.length === 0}
          />
          <div className={styles.toolMain}>
            <span className={sharedStyles.muted}>
              {visible.length === tools.length
                ? `All ${tools.length} tools`
                : `${visible.length} of ${tools.length} shown`}
            </span>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className={styles.toolRow}>
            <span className={sharedStyles.muted}>No tools match this search or filter.</span>
          </div>
        ) : (
          visible.map((tool) => {
            const badges = getToolBadges(tool);
            const isPending = pending.has(tool.tool_name);
            const isDuplicate = duplicates.has(tool.tool_name);
            const schemaReason = invalidReason(tool);

            return (
              <div
                key={tool.id ?? tool.tool_name}
                id={tool.id != null ? `mcp-tool-${tool.id}` : undefined}
                className={`${styles.toolRow} ${
                  highlightId === String(tool.id) ? styles.toolRowFocus : ''
                }`}
              >
                {isPending ? (
                  <Box sx={{ width: 38, display: 'flex', justifyContent: 'center', pt: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : (
                  <Checkbox
                    size="small"
                    checked={!!tool.enabled}
                    onChange={(e) => setEnabled([tool.tool_name], e.target.checked)}
                    disabled={bulkBusy}
                  />
                )}

                <div className={styles.toolMain}>
                  <div className={styles.toolNameRow}>
                    <span className={styles.toolName}>{tool.tool_name}</span>
                    {badges.map((b) => (
                      <Tooltip key={b.key} title={b.title}>
                        <span className={`${styles.badge} ${BADGE_CLASS[b.tone]}`}>
                          {b.tone === 'danger' && <AlertTriangle size={10} />}
                          {b.label}
                        </span>
                      </Tooltip>
                    ))}
                    {schemaReason && (
                      <Tooltip title={INVALID_SCHEMA_COPY.badgeTooltip(schemaReason)} arrow>
                        <span className={`${styles.badge} ${styles.badgeDanger}`}>
                          <AlertTriangle size={10} />
                          {INVALID_SCHEMA_COPY.badge}
                        </span>
                      </Tooltip>
                    )}
                    {isDuplicate && (
                      <span className={styles.dupWarning}>duplicate name — publishing is blocked</span>
                    )}
                  </div>
                  <span
                    className={`${styles.toolDesc} ${
                      tool.description ? '' : styles.toolDescMissing
                    }`}
                  >
                    {tool.description || 'No description — models will struggle to call this correctly.'}
                  </span>
                </div>

                <div className={styles.toolActions}>
                  <Tooltip title="Test this tool">
                    <IconButton
                      size="small"
                      className={sharedStyles.subtleBtn}
                      onClick={() => onTestTool(tool.id)}
                    >
                      <FlaskConical size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit name, description and schema">
                    <IconButton
                      size="small"
                      className={sharedStyles.subtleBtn}
                      onClick={() => setEditingTool(tool)}
                    >
                      <Pencil size={16} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })
        )}
      </div>

      <ToolEditDrawer
        open={!!editingTool}
        server={server}
        tool={editingTool}
        lintReason={invalidReason(editingTool)}
        onClose={() => setEditingTool(null)}
        onSaved={async () => {
          setEditingTool(null);
          await onMutated();
        }}
        onTest={(toolId) => {
          setEditingTool(null);
          onTestTool(toolId);
        }}
        notify={notify}
      />

      <EnrichDiffDialog
        open={enrichOpen}
        server={server}
        onClose={() => setEnrichOpen(false)}
        onApplied={async () => {
          setEnrichOpen(false);
          await onMutated();
        }}
        notify={notify}
      />
    </>
  );
};

export default CurateToolsTab;
